// Clerk integration (issue #464):
//
// When VITE_CLERK_PUBLISHABLE_KEY (or window.LUCID_CLERK_PUBLISHABLE_KEY)
// is set we wrap the app in <ClerkProvider> and gate /play behind a
// signed-in session. When unset we fall back to a plain <BrowserRouter>
// so the UI keeps working against an engine running with
// LUCID_AUTH_DEV_BYPASS=1 — see web/src/auth/clerk.ts for the contract.
import React, { type ReactNode } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ClerkProvider } from "@clerk/clerk-react";

import Landing from "./routes/Landing";
import Play from "./routes/Play";
import SignInPage from "./routes/SignIn";
import SignUpPage from "./routes/SignUp";
import WorldPicker from "./routes/WorldPicker";
import RequireAuth from "./auth/RequireAuth";
import TokenBridge from "./auth/TokenBridge";
import { getPublishableKey, hasClerk } from "./auth/clerk";
import "./styles/index.css";

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route
        path="/play"
        element={
          <RequireAuth>
            <Play />
          </RequireAuth>
        }
      />
      <Route
        path="/worlds"
        element={
          <RequireAuth>
            <WorldPicker />
          </RequireAuth>
        }
      />
      {hasClerk() && (
        <>
          <Route path="/sign-in/*" element={<SignInPage />} />
          <Route path="/sign-up/*" element={<SignUpPage />} />
        </>
      )}
    </Routes>
  );
}

function Root({ children }: { children: ReactNode }) {
  const pk = getPublishableKey();
  if (!pk) {
    // Dev-bypass build: no Clerk, no token bridge, no auth gate.
    return <BrowserRouter>{children}</BrowserRouter>;
  }
  return (
    <ClerkProvider publishableKey={pk}>
      <BrowserRouter>
        <TokenBridge />
        {children}
      </BrowserRouter>
    </ClerkProvider>
  );
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <Root>
      <AppRoutes />
    </Root>
  </React.StrictMode>,
);
